import { useState } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

const C = { green: '#3cb74e', black: '#111111', white: '#ffffff', red: '#ef4444', amber: '#f59e0b', gray: '#64748b', border: '#e2e8f0', lightBg: '#f8fdf8', navy: '#0f1e45' };
const statusColors = {
  valid:   { bg: '#f0fdf4', text: '#166534', border: '#86efac' },
  invalid: { bg: '#fef2f2', text: '#991b1b', border: '#fca5a5' },
  risky:   { bg: '#fffbeb', text: '#92400e', border: '#fde68a' },
  unknown: { bg: '#f8fafc', text: '#475569', border: '#cbd5e1' },
};
const statusIcons = { valid: '✅', invalid: '❌', risky: '⚠️', unknown: '❓' };

export default function SingleVerifier() {
  const [email, setEmail] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { updateUser } = useAuth();

  const handleVerify = async () => {
    if (!email.trim()) return setError('Please enter an email address');
    setError(''); setResult(null); setLoading(true);
    try {
      const res = await api.post('/email/verify', { email });
      setResult(res.data.result);
      if (res.data.usage) updateUser(res.data.usage);
    } catch (err) {
      setError(err.response?.data?.message || 'Verification failed');
    } finally { setLoading(false); }
  };

  return (
    <div style={s.container}>
      <div style={s.inputRow}>
        <input
          style={s.input}
          type="email"
          placeholder="Enter email address to verify..."
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleVerify()}
        />
        <button style={{ ...s.btn, opacity: loading ? 0.7 : 1 }} onClick={handleVerify} disabled={loading}>
          {loading ? '⏳ Verifying...' : '🔍 Verify'}
        </button>
      </div>
      {error && <div style={s.errorBox}>⚠️ {error}</div>}

      {result && (() => {
        const sc = statusColors[result.status];
        return (
          <div style={{ ...s.resultCard, background: sc.bg, border: `1.5px solid ${sc.border}` }}>
            <div style={s.resultTop}>
              <div style={s.resultLeft}>
                <span style={s.resultIcon}>{statusIcons[result.status]}</span>
                <div>
                  <p style={s.resultEmail}>{result.email}</p>
                  <span style={{ ...s.resultBadge, background: sc.text, color: C.white }}>
                    {result.status.toUpperCase()}
                    {result.fromCache && <span style={s.cacheBadge}> ⚡ cached</span>}
                  </span>
                </div>
              </div>
            </div>
            <div style={s.checksGrid}>
              {[
                { label: 'Format',        value: result.format_valid,    detail: result.details?.format },
                { label: 'MX Record',     value: result.mx_valid,        detail: result.details?.mx },
                { label: 'SMTP',          value: result.smtp_valid,      detail: result.details?.smtp },
                { label: 'Not Disposable',value: !result.is_disposable,  detail: result.details?.disposable },
                { label: 'Not Catch-all', value: !result.is_catchall,    detail: result.details?.catchall },
              ].map(({ label, value, detail }) => (
                <div key={label} style={s.checkCard}>
                  <span style={{ fontSize: 18 }}>{value ? '✅' : '❌'}</span>
                  <div>
                    <p style={s.checkLabel}>{label}</p>
                    {detail && <p style={s.checkDetail}>{detail}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

const s = {
  container: { background: C.white, borderRadius: 14, border: `1.5px solid ${C.border}`, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  inputRow: { display: 'flex', gap: 12, marginBottom: 16 },
  input: { flex: 1, padding: '13px 16px', borderRadius: 9, border: `2px solid ${C.border}`, fontSize: 15, outline: 'none', color: C.black, background: C.white, transition: 'border-color .2s' },
  btn: { padding: '13px 28px', background: C.green, color: C.white, border: 'none', borderRadius: 9, fontWeight: 700, fontSize: 14, cursor: 'pointer', letterSpacing: '.3px' },
  errorBox: { background: '#fef2f2', border: '1.5px solid #fca5a5', color: '#991b1b', padding: '10px 16px', borderRadius: 8, fontSize: 13, marginBottom: 12 },
  resultCard: { borderRadius: 12, padding: 20 },
  resultTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  resultLeft: { display: 'flex', alignItems: 'center', gap: 14 },
  resultIcon: { fontSize: 32 },
  resultEmail: { color: C.black, fontWeight: 700, fontSize: 16, margin: '0 0 6px' },
  resultBadge: { padding: '3px 12px', borderRadius: 12, fontSize: 12, fontWeight: 700 },
  cacheBadge: { fontSize: 10, fontWeight: 400, opacity: 0.8 },
  checksGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 },
  checkCard: { display: 'flex', gap: 10, alignItems: 'flex-start', background: 'rgba(255,255,255,0.75)', borderRadius: 8, padding: '10px 12px' },
  checkLabel: { color: C.black, fontWeight: 600, fontSize: 13, margin: '0 0 2px' },
  checkDetail: { color: C.gray, fontSize: 11, margin: 0 },
};
